import { type Breakpoint } from '@divi/types';

import { FITVIDS_CUSTOM_SELECTOR } from '../../../fitvids-functions/constants';
import { getFitvidsIframe } from '../../../jquery.fitvids/get-fitvids-iframe';
import { getFitvidsIframeContent } from '../../../jquery.fitvids/get-fitvids-iframe-content';
import {
  type MultiViewDataItemContent,
  type MultiViewDataUpdate,
  type MultiViewDataUpdateContent,
  type MultiViewUpdateElementParams,
  type PseudoStateDataMap,
  type RuntimePseudoEvent,
  type RuntimePseudoState,
} from '../../types';
import { createSwitchDataToCurrentPseudoState } from '../create-switch-data-to-current-pseudo-state';
import { dispatchCustomEvent } from '../dispatch-custom-event';
import { hasPseudoStateData } from '../has-pseudo-state-data';
import { parseData } from '../parse-data';
import { registerPseudoStateListeners } from '../register-pseudo-state-listeners';
import {
  getVideoContentFromElement,
  getVideoContentFromStateData,
  isVideoContentUpdateNeeded,
  shouldPreserveVideoBoxContent,
  stateDataContainsIframe,
} from '../video-content';

const VIDEO_MODULE_NAME = 'divi/video';
const VIDEO_BOX_CLASS = 'et_pb_video_box';

const stickyClassName = 'et_pb_sticky';
const stickyStartEventName = 'ETBuilderStickyStart';
const stickyEndEventName = 'ETBuilderStickyEnd';
const videoContentStateMap = new WeakMap<HTMLElement, string>();
const videoContentIframeMap = new WeakMap<HTMLElement, HTMLIFrameElement>();
const videoContentMarkupMap = new WeakMap<HTMLElement, string>();
const svgStickyEventCleanupMap = new WeakMap<HTMLElement, WeakMap<HTMLElement, () => void>>();

/**
 * Whether the trigger or an ancestor is currently sticky.
 *
 * Child modules inherit sticky context from a sticky parent (section/row).
 *
 * @since ??
 *
 * @param {HTMLElement} trigger MultiView hover/trigger element.
 *
 * @returns {boolean}
 */
const hasStickyContext = (trigger: HTMLElement): boolean => null !== trigger.closest(`.${stickyClassName}`);

/**
 * Remove previously registered SVG sticky window listeners for these triggers.
 *
 * @since ??
 *
 * @param {NodeListOf<HTMLElement>|HTMLElement[]} triggerElements MultiView hover/trigger elements.
 * @param {HTMLElement} target MultiView content target.
 */
const unregisterSvgStickyContentListeners = (
  triggerElements: NodeListOf<HTMLElement> | HTMLElement[],
  target: HTMLElement,
) => {
  triggerElements.forEach(hoverElement => {
    const existingCleanup = svgStickyEventCleanupMap.get(hoverElement)?.get(target);

    if (existingCleanup) {
      existingCleanup();
    }
  });
};

/**
 * Set classes to to hide element when the innerHTML is empty.
 *
 * @since ??
 *
 * @param {string} innerHTML A string representing the inner HTML of an element.
 * @param {HTMLElement} element The element target.
 * @param {MultiViewDataUpdate} item The data item being updated.
 * @param {string} state The current state.
 */
const setElementHiddenClass = (innerHTML: string, element: HTMLElement, item: MultiViewDataUpdate, state: string) => {
  if ('' === innerHTML) {
    // Only hide element if it's in default state AND there's no pseudo-state content available.
    if ('default' === state) {
      const hasPseudoStateContent =
        item?.data && 'object' === typeof item.data
          ? Object.keys(item.data).some(key => {
              if (/--(hover|focus|checked|active|sticky)$/.test(key)) {
                const value = item.data[key as keyof typeof item.data];
                return value && 'string' === typeof value && value.trim() !== '';
              }
              return false;
            })
          : false;

      if (!hasPseudoStateContent) {
        element.classList.add('et_multi_view_hidden');
      }
    }
  } else if (element.classList.contains('et_multi_view_hidden')) {
    element.classList.remove('et_multi_view_hidden');
  }
};

/**
 * Check whether the update target is a Video module video box.
 *
 * @since ??
 *
 * @param {MultiViewDataUpdateContent} item The data item being updated.
 * @param {HTMLElement} element The element target.
 *
 * @returns {boolean} Whether video-aware content updates should be used.
 */
const isVideoModuleVideoBox = (item: MultiViewDataUpdateContent, element: HTMLElement): boolean =>
  VIDEO_MODULE_NAME === item.moduleName && element.classList.contains(VIDEO_BOX_CLASS);

/**
 * Re-apply fitVids to a video box after a genuine iframe swap.
 *
 * @since ??
 *
 * @param {HTMLElement} element The `.et_pb_video_box` element.
 */
const applyFitVidsToVideoBox = (element: HTMLElement): void => {
  const { jQuery } = window;

  if (!jQuery?.fn?.fitVids) {
    return;
  }

  jQuery(element).fitVids({
    customSelector: FITVIDS_CUSTOM_SELECTOR,
  });
};

/**
 * Sets the inner HTML of an element and dispatches a custom event.
 *
 * @since ??
 *
 * @param {object} params The parameters needed to set the inner HTML of an element.
 * @param {MultiViewDataItemContent} params.stateData The data to set as the inner HTML.
 * @param {MultiViewDataUpdateContent} params.item The data item being updated.
 * @param {HTMLElement} params.element The element to update.
 * @param {Breakpoint.Name} params.breakpoint The current breakpoint.
 * @param {string} params.state The state of the element.
 * @param {MouseEvent | TouchEvent} params.originalEvent The original event that triggered the update.
 */
const setElementInnerHTML = ({
  stateData,
  item,
  element,
  breakpoint,
  state,
  originalEvent,
}: MultiViewUpdateElementParams<MultiViewDataItemContent, MultiViewDataUpdateContent>) => {
  let updatedData: MultiViewDataItemContent;
  let updated = false;

  const isVideoContent = 'divi/video' === item.moduleName && element.classList.contains('et_pb_video_box');
  const videoIframe = isVideoContent ? element.querySelector('iframe') : null;

  // FitVids can initialize before or after MultiView. Prime only the first matching live player.
  if (videoIframe && getFitvidsIframe(element) && !videoContentStateMap.has(element)) {
    const template = element.ownerDocument.createElement('template');
    template.innerHTML = stateData;
    const expectedIframe = template.content.querySelector('iframe');

    if (expectedIframe && getFitvidsIframeContent(videoIframe) === getFitvidsIframeContent(expectedIframe)) {
      videoContentStateMap.set(element, stateData);
      videoContentIframeMap.set(element, videoIframe);
      videoContentMarkupMap.set(element, getFitvidsIframeContent(videoIframe));
    }
  }
  const hasUnchangedVideo =
    videoIframe &&
    videoContentStateMap.get(element) === stateData &&
    videoContentIframeMap.get(element) === videoIframe &&
    videoContentMarkupMap.get(element) === getFitvidsIframeContent(videoIframe);

  // FitVids changes responsive markup. Preserve its initialized player until the source or DOM changes.
  const hasStaleVideo = isVideoContent && videoContentStateMap.has(element) && !hasUnchangedVideo;
  const shouldUpdateContent = (): boolean => {
    if (!isVideoModuleVideoBox(item, element)) {
      return element.innerHTML !== stateData;
    }

    if (shouldPreserveVideoBoxContent(element)) {
      return false;
    }

    if (hasUnchangedVideo) {
      return false;
    }

    // Repair replaced or externally changed players only after playback/fullscreen
    // preservation has had the opportunity to keep the active player intact.
    if (hasStaleVideo && videoContentStateMap.get(element) === stateData) {
      return true;
    }

    const currentContent = getVideoContentFromElement(element);
    const newContent = getVideoContentFromStateData(stateData);

    if ('unknown' === currentContent.type || 'unknown' === newContent.type) {
      return element.innerHTML !== stateData;
    }

    const needsUpdate = isVideoContentUpdateNeeded(currentContent, newContent);
    if (!needsUpdate && videoIframe) {
      // Different breakpoint markup may resolve to the same normalized video source.
      // Remember that state so repeated resizes can reuse the live-player snapshot.
      videoContentStateMap.set(element, stateData);
      videoContentIframeMap.set(element, videoIframe);
      videoContentMarkupMap.set(element, getFitvidsIframeContent(videoIframe));
    }
    return needsUpdate;
  };

  if (shouldUpdateContent()) {
    setElementHiddenClass(stateData, element, item, state);
    element.innerHTML = stateData;
    updated = true;
    updatedData = stateData;

    if (isVideoModuleVideoBox(item, element) && stateDataContainsIframe(stateData)) {
      applyFitVidsToVideoBox(element);
    }
  }

  if (updated) {
    // Dispatch custom event: divi:multiView:setContent
    dispatchCustomEvent<MultiViewDataItemContent>({
      breakpoint,
      item,
      originalEvent,
      state,
      updatedData,
    });

    if (isVideoContent) {
      const iframe = element.querySelector('iframe');
      if (iframe) {
        videoContentStateMap.set(element, stateData);
        videoContentIframeMap.set(element, iframe);
        videoContentMarkupMap.set(element, getFitvidsIframeContent(iframe));
      } else {
        videoContentStateMap.delete(element);
        videoContentIframeMap.delete(element);
        videoContentMarkupMap.delete(element);
      }
    }
  }
};

/**
 * Update element inner HTML.
 *
 * @since ??
 *
 * @param {object} params The parameters needed to update the inner HTML of an element.
 * @param {MultiViewDataUpdateContent} params.item The item that will be updated.
 * @param {Breakpoint.Name} params.breakpoint The current breakpoint, which can be 'phone', 'tablet', or 'desktop'.
 * @param {boolean} params.isInit Whether this is the first time the function is being called.
 * @param {Breakpoint.Name} params.baseBreakpoint The base breakpoint.
 * @param {Breakpoint.Names} params.breakpointNames The list of breakpoint names.
 */
export const updateDataContent = ({
  item,
  breakpoint,
  isInit = false,
  baseBreakpoint = 'desktop',
  breakpointNames = ['desktop', 'tablet', 'phone'],
}: {
  item: MultiViewDataUpdateContent;
  breakpoint: Breakpoint.Name;
  isInit: boolean;
  baseBreakpoint: Breakpoint.Name;
  breakpointNames: Breakpoint.Names;
}): void => {
  const { element, hoverElements, data } = item;
  const { defaultStateData, hoverStateData, focusStateData, checkedStateData, activeStateData, stickyStateData } =
    parseData<MultiViewDataItemContent>({
      data,
      breakpoint,
      baseBreakpoint,
      breakpointNames,
    });

  // Bail early default state data is null.
  // This can happen if the data is not valid.
  if (null === defaultStateData) {
    return;
  }

  const svgStickyMarkup = 'divi/svg' === item.moduleName && null !== stickyStateData ? stickyStateData : null;

  const setStateData = (stateData: MultiViewDataItemContent, state: RuntimePseudoState, event?: RuntimePseudoEvent) => {
    setElementInnerHTML({
      stateData,
      state,
      element,
      item,
      breakpoint,
      originalEvent: event,
    });
  };

  const applySvgStickyMarkup = (event?: RuntimePseudoEvent) => {
    if (null === svgStickyMarkup) {
      return;
    }

    setElementInnerHTML({
      stateData: svgStickyMarkup,
      state: 'sticky',
      element,
      item,
      breakpoint,
      originalEvent: event,
    });
  };

  const stateDataMap: PseudoStateDataMap<MultiViewDataItemContent> = {
    defaultStateData,
    hoverStateData,
    focusStateData,
    checkedStateData,
    activeStateData,
  };

  const switchDataToDefaultState = (event?: RuntimePseudoEvent) => {
    setStateData(defaultStateData, 'default', event);
  };

  const switchDataToCurrentPseudoState = createSwitchDataToCurrentPseudoState({
    stateDataMap,
    setStateData,
  });

  const onPseudoStateChange = (
    hoverElement: HTMLElement,
    event?: RuntimePseudoEvent,
    forcedState?: RuntimePseudoState,
  ) => {
    if (null !== svgStickyMarkup && hasStickyContext(hoverElement)) {
      applySvgStickyMarkup(event);
      return;
    }

    switchDataToCurrentPseudoState(hoverElement, event, forcedState);
  };

  const applySvgStickyContentIfActive = () => {
    if (null === svgStickyMarkup) {
      return;
    }

    hoverElements.forEach(hoverElement => {
      if (hasStickyContext(hoverElement)) {
        applySvgStickyMarkup();
      }
    });
  };

  const registerSvgStickyContentListeners = () => {
    hoverElements.forEach(hoverElement => {
      const cleanupByElement = svgStickyEventCleanupMap.get(hoverElement) ?? new WeakMap<HTMLElement, () => void>();
      const existingCleanup = cleanupByElement.get(element);

      if (existingCleanup) {
        existingCleanup();
      }

      let unregisterSvgStickyContentListener = () => {};

      const onStickyChangeCallback = (event: Event) => {
        if (!hoverElement.isConnected || !element.isConnected) {
          unregisterSvgStickyContentListener();
          return;
        }

        onPseudoStateChange(hoverElement, event);
      };

      unregisterSvgStickyContentListener = () => {
        window.removeEventListener(stickyStartEventName, onStickyChangeCallback);
        window.removeEventListener(stickyEndEventName, onStickyChangeCallback);
        cleanupByElement.delete(element);
      };

      window.addEventListener(stickyStartEventName, onStickyChangeCallback);
      window.addEventListener(stickyEndEventName, onStickyChangeCallback);

      cleanupByElement.set(element, unregisterSvgStickyContentListener);
      svgStickyEventCleanupMap.set(hoverElement, cleanupByElement);
    });
  };

  // Handle pseudo states.
  if (hasPseudoStateData(stateDataMap)) {
    registerPseudoStateListeners({
      element,
      hoverElements,
      stateDataMap,
      onPseudoStateChange,
      listenerNamespace: 'update-data-content',
    });
  }

  unregisterSvgStickyContentListeners(hoverElements, element);

  if (null !== svgStickyMarkup) {
    registerSvgStickyContentListeners();
  }

  // Switch to default state on page load or when the breakpoint changes that triggered when the user
  // resizing the browser or changing the device orientation.
  if ('desktop' === breakpoint && isInit && !item.switchOnLoad) {
    applySvgStickyContentIfActive();
    return;
  }

  switchDataToDefaultState();
  applySvgStickyContentIfActive();
};
